import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';

import { Duration } from 'luxon';
import moment from 'moment-timezone';

import { AvailabilityWidget } from '@admin/components/availability_widget/availability_widget';
import { availabilities, pricingPerItemFee } from '@admin/utils';
import { ORDER_TYPE_MAP, ORDER_SUBTYPE_MAP } from '@admin/utils/order_type_map';

import { Schedule, IScheduleEvent, IPerItemFee } from './schedule';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const NY_INTRASTATE_LAUNCH_DATE = '2021-02-05';
const REFRESH_INTERVAL_MS = 15000;
const DEBOUNCE_MS = 700;

interface ILaborRate {
  id: string;
  amount: number;
}

interface IOrder {
  id?: number;
  type: string;
  subtype?: string;
  region?: {
    id: number;
    tz?: string;
  };
  predecessor_id?: string | null;
  account_id?: number;
  address?: {
    id?: number;
    aptsuite?: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    building_type?: string;
    building_subtype?: string;
    floor: number | null;
    stories: number | null;
    front_door_steps: number | null;
    squareFeet: number | null;
    stairs?: boolean;
    elevator?: boolean;
    gated?: boolean;
    steep?: boolean;
    service_elevator?: boolean;
    service_entrance?: boolean;
    loading_dock?: boolean;
    parking_street?: boolean;
    parking_driveway?: boolean;
    parking_lot?: boolean;
    parking_alley?: boolean;
    parking_permit?: boolean;
    parking_in_front?: boolean;
    parking_on_specific_street: string | null;
    parking_instructions: string | null;
    max_truck_height: number | null;
    max_truck_height_unknown?: boolean;
    paperwork?: boolean;
    access_unknown?: boolean;
    paperwork_unknown?: boolean;
    environment: string | null;
    parking_unknown?: boolean;
    additional_notes: string | null;
    building_opening_hour: number | null;
    building_closing_hour: number | null;
    building_restrictions_answer: string | null;
    code?: boolean;
    code_value: string | null;
  };
  items?: Array<{ id: number | string }>;
  inventory?: object;
  labor_rate_id?: string;
  per_mover_hour_adjustment_amount?: number | null;
  scheduled?: Date;
  scheduled_override?: boolean;
  duration?: number;
  moving_operation?: {
    destination_address?: {
      state?: string;
    };
    origin_address?: {
      state?: string;
    };
  };
}

interface ISubscription {
  plan_id?: number;
  plan?: {
    id: number;
    type?: string;
  };
  quantity: number;
  custom_plan?: boolean;
  dimensions?: {
    length?: string;
    width?: string;
  };
}

interface IOrderAvailabilityProps {
  order: IOrder;
  subscriptions: ISubscription[];
  timezone?: string;
  onScheduleChange?(): void;
}

/**
 * Converts subscriptions to the format needed for availability parameters.
 *
 * @param subscriptions - Array of subscription objects
 * @returns Cleaned subscription array for availability API
 */
const getSubscriptionsForAvailabilityParameters = (
  subscriptions: ISubscription[] | undefined,
): Array<{
  plan_id: number;
  quantity: number;
  custom_plan?: boolean;
  custom_plan_dimension_length?: string;
  custom_plan_dimension_width?: string;
}> => {
  if (!subscriptions) {
    return [];
  }
  return subscriptions
    .map((subscription) => {
      let customDimensionLength: string | null = null;
      let customDimensionWidth: string | null = null;

      if (
        subscription &&
        subscription.custom_plan &&
        subscription.dimensions &&
        subscription.dimensions.length &&
        subscription.dimensions.width
      ) {
        customDimensionLength = subscription.dimensions.length.toString();
        customDimensionWidth = subscription.dimensions.width.toString();
      }

      return {
        plan_id: subscription.plan_id || subscription.plan?.id || 0,
        quantity: subscription.quantity,
        type: subscription.plan?.type,
        custom_plan: subscription.custom_plan,
        custom_plan_dimension_length: customDimensionLength || undefined,
        custom_plan_dimension_width: customDimensionWidth || undefined,
      };
    })
    .filter(
      (subscription) =>
        subscription.quantity > 0 &&
        !!subscription.plan_id &&
        (subscription.type === 'StoragePlan' || !subscription.type),
    )
    .map(({ type, ...rest }) => rest);
};

export const OrderAvailability: React.FC<IOrderAvailabilityProps> = ({
  order,
  subscriptions,
  timezone: propTimezone,
  onScheduleChange,
}) => {
  const timezone = propTimezone || order?.region?.tz || 'UTC';

  const [dates, setDates] = useState<Date[]>([]);
  const [selectedDays, setSelectedDays] = useState<Date[]>([]);
  const [events, setEvents] = useState<IScheduleEvent[]>([]);
  const [perItemFee, setPerItemFee] = useState<IPerItemFee | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>(undefined);

  const loadingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevRegionIdRef = useRef<number | undefined>(order?.region?.id);

  const isDateInDates = useCallback(
    (d1: Date) => dates.some((d2) => moment.tz(d1, timezone).isSame(moment.tz(d2, timezone), 'day')),
    [dates, timezone],
  );

  const setAvailabilityEvents = useCallback(
    (
      entries: Array<{
        datetime: { toISO(): string; toJSDate(): Date; plus(duration: unknown): { toJSDate(): Date } };
        available: boolean;
        duration?: Duration;
        appointmentFee?: string | null;
        laborRate?: ILaborRate | null;
        perMoverHourAdjustmentAmount?: number | null;
        laborBillingFormat?: string | null;
      }>,
      isNYIntraStateMove: boolean,
    ) => {
      const filteredEvents = entries
        .filter((entry) => !!entry.duration)
        .filter((entry) => isDateInDates(new Date(entry.datetime.toJSDate())))
        .filter(
          (entry) =>
            !isNYIntraStateMove ||
            moment
              .tz(NY_INTRASTATE_LAUNCH_DATE, timezone)
              .isBefore(moment.tz(new Date(entry.datetime.toJSDate()), timezone), 'day'),
        )
        .map((entry) => ({
          // Use start time + duration to create unique ID for events with same start time
          id: `${entry.datetime.toISO()}_${entry.duration!.valueOf()}`,
          available: entry.available,
          from_time: entry.datetime.toJSDate(),
          till_time: entry.datetime.plus(entry.duration!).toJSDate(),
          duration: entry.duration!,
          appointmentFee: entry.appointmentFee,
          laborRate: entry.laborRate,
          perMoverHourAdjustmentAmount: entry.perMoverHourAdjustmentAmount,
          laborBillingFormat: entry.laborBillingFormat,
        }));
      setEvents(filteredEvents);
    },
    [isDateInDates, timezone],
  );

  const loadResources = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    const from = dates.length > 0 ? dates.reduce((a, b) => (a < b ? a : b)) : undefined;
    const till = dates.length > 0 ? dates.reduce((a, b) => (a > b ? a : b)) : undefined;

    if (!from || !till) {
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    try {
      const cleanedSubscriptions = getSubscriptionsForAvailabilityParameters(subscriptions);

      const availResult = await availabilities({
        accountID: order.account_id,
        orderID: order.id,
        predecessorID: order.predecessor_id,
        regionID: order.region?.id,
        type: order.type as keyof typeof ORDER_TYPE_MAP,
        address: order.address as Parameters<typeof availabilities>[0]['address'],
        subscriptions: cleanedSubscriptions as Parameters<typeof availabilities>[0]['subscriptions'],
        items: order.items,
        inventory: order.inventory,
        from: moment.tz(from, timezone).format(ISO_DATE_FORMAT),
        till: moment.tz(till, timezone).format(ISO_DATE_FORMAT),
      });

      const data = 'data' in availResult ? availResult.data : undefined;
      const availError = 'error' in availResult ? availResult.error : undefined;

      const perItemFeeResult = await pricingPerItemFee({
        regionID: order.region?.id ? String(order.region.id) : null,
        orderType: order.type as keyof typeof ORDER_TYPE_MAP,
        orderSubtype: (order.subtype || 'none') as keyof typeof ORDER_SUBTYPE_MAP,
      });

      const perItemFeeData = 'data' in perItemFeeResult ? perItemFeeResult.data : undefined;
      const perItemFeeError = 'error' in perItemFeeResult ? perItemFeeResult.error : undefined;

      if (availError || perItemFeeError) {
        setError([availError, perItemFeeError].filter(Boolean).join(' '));
      } else {
        const isNYIntraStateMove =
          order.type === 'moving-move' &&
          order.moving_operation?.destination_address?.state === 'NY' &&
          order.moving_operation?.origin_address?.state === 'NY';
        if (data) {
          setAvailabilityEvents(data, isNYIntraStateMove);
        }
        if (perItemFeeData) {
          setPerItemFee(perItemFeeData);
        }
        setError(undefined);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [dates, order, subscriptions, setAvailabilityEvents, timezone]);

  const loadResourcesDebounced = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      loadResources();
    }, DEBOUNCE_MS);
  }, [loadResources]);

  const checkChanges = useCallback(() => {
    setError(undefined);
    loadResourcesDebounced();
  }, [loadResourcesDebounced]);

  // Refresh availability every 15 seconds
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      checkChanges();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [checkChanges]);

  // Clear pending debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle region changes - clear selection when region changes
  useEffect(() => {
    if (prevRegionIdRef.current !== undefined && order?.region?.id !== prevRegionIdRef.current) {
      setSelectedEvent(undefined);
    }
    prevRegionIdRef.current = order?.region?.id;
    checkChanges();
  }, [order?.region?.id, order?.type, order?.subtype, order?.address, order?.items, order?.inventory, checkChanges]);

  const onCheck = useCallback(() => {
    setDates(selectedDays);
    checkChanges();
  }, [selectedDays, checkChanges]);

  const onDaySelected = useCallback((newDates: Date[]) => {
    setSelectedDays(newDates);
  }, []);

  const getFooterMessage = useCallback((): string => {
    if (selectedEvent) {
      // Extract the datetime from the ID (format: "datetime_duration")
      const dateTimeStr = selectedEvent.split('_')[0];
      const selected = moment.tz(dateTimeStr, timezone);
      return `You're booking ${selected.format('h:mm a [on] ddd, MMM D')}`;
    }
    return '';
  }, [selectedEvent, timezone]);

  const scheduleEventClicked = useCallback(
    ({
      id,
      duration,
      laborRate,
      perMoverHourAdjustmentAmount,
    }: {
      id: string;
      duration: { valueOf(): number };
      laborRate?: ILaborRate | null;
      perMoverHourAdjustmentAmount?: number | null;
    }) => {
      if (!id) return;

      setSelectedEvent(id);

      // Update order properties (these updates happen on the parent order object)
      if (order) {
        order.labor_rate_id = laborRate?.id;
        order.per_mover_hour_adjustment_amount = perMoverHourAdjustmentAmount;
        // Extract the datetime from the ID (format: "datetime_duration")
        const dateTimeStr = id.split('_')[0];
        order.scheduled = new Date(dateTimeStr);
        order.scheduled_override = false;
        order.duration = duration.valueOf() / 1000; // Convert to seconds
      }

      if (onScheduleChange) {
        onScheduleChange();
      }
    },
    [order, onScheduleChange],
  );

  return (
    <div className="availability-container">
      {error && (
        <div className="availability-error">
          <span className="text-danger">{error}</span>
        </div>
      )}
      <div className="row">
        <div className="col-sm-6">
          <AvailabilityWidget
            onCheck={onCheck}
            disabled={!!error}
            leadId={0}
            onDaySelected={onDaySelected}
            selectedDays={selectedDays}
            planId={subscriptions?.[0]?.plan_id || subscriptions?.[0]?.plan?.id}
            zip={order?.address?.zip}
          />
        </div>
        <div className="col-sm-6">
          {dates.length > 0 && !error && (
            <div className="availability-calendar-container">
              <Schedule
                untouchedMessage="Fill in job details"
                selectedEvent={selectedEvent}
                dates={dates}
                emptyDayMessage="No time slots available for this job."
                className="availability-schedule"
                events={events}
                perItemFee={perItemFee}
                timezone={timezone}
                onScheduleEventClick={scheduleEventClicked}
                footerMessage={getFooterMessage()}
                loading={loading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
